import React from 'react';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import slugify from '@bostonglobe/js-slugify';
import {
  find,
  get,
} from 'lodash';

import useBusiness from '../lib/useBusiness';

import pageStyles from '../styles/pages/index.module.styl';
import articles from '../../content/articles.json';
import Layout from '../components/layout';
import StoryTile from '../components/StoryTile';
import makeShuffledArray from '../lib/makeShuffledArray';
import Sponsor from '../components/Sponsor';
import Seo from '../components/seo';
import { homepage } from '../../package.json';

const backupImage = 'arc-anglerfish-arc2-prod-bostonglobe/public/NK2C2JTRVII6RCTVNGPDN5O6DA.jpg';

const IndexPage = () => {
  const data = useStaticQuery(graphql`
      query IndexPageQuery {
          socialImage: file(name: {eq: "landing-page-social"}) {
              childImageSharp {
                  fixed(width: 1200) {
                      src
                  }
              }
          }
          site {
              siteMetadata {
                  title
                  description
                  defaultSocial
                  eventDescription
                  eventLinkText
                  eventUrl
                  eventExpiry
              }
          }
          allArcStory {
              nodes {
                  storyId
                  headlines {
                      basic
                      web
                  }
                  overline
                  promo_items {
                      basic {
                          url
                      }
                  }
              }
          }
      }
  `);
  const [displayFrame, setDisplayFrame] = React.useState(0);
  const [shuffledOrder, setShuffledOrder] = React.useState([]);

  useBusiness({
    environment: process.env.NODE_ENV,
    meterContent: false,
    pageId: 'apps.future-of-food-landing',
    section: 'ideas',
    author: '',
  });

  // Using state so that the shuffle only occurs once.
  React.useEffect(
    () => {
      setShuffledOrder(makeShuffledArray(articles.length));
    },
    [],
  );

  React.useEffect(
    () => {
      let animationTimeout;
      if (displayFrame < articles.length) {
        animationTimeout = setTimeout(
          () => {
            setDisplayFrame(displayFrame + 1);
          },
          75,
        );
      }

      return () => {
        clearTimeout(animationTimeout);
      };
    },
    [displayFrame],
  );

  return (
    <Layout
      path="/"
      className="page-index"
      pageTitle={data.site.siteMetadata.title}
      socialText={data.site.siteMetadata.defaultSocial}
    >
      <Seo
        title={data.site.siteMetadata.title}
        description={data.site.siteMetadata.defaultSocial}
        meta={[
          {
            property: 'og:image',
            // Hard-coded to use the production URL, so this won't work till deployed.
            content: `https://apps.bostonglobe.com${data.socialImage.childImageSharp.fixed.src}`,
          },
        ]}
      />
      <main className={pageStyles.spread} id="content">
        <div className={pageStyles.content}>
          <h1 className={classNames(pageStyles.headline, 'millerbanner-n4')}>
            {data.site.siteMetadata.title}
          </h1>
          <p className={pageStyles.dek}>
            {data.site.siteMetadata.description}
          </p>
          {(new Date() <= new Date(data.site.siteMetadata.eventExpiry)) && (
            <p className={pageStyles.eventTease}>
              {data.site.siteMetadata.eventDescription}
              {' '}
              <a
                href={data.site.siteMetadata.eventUrl}
                target="_blank"
                rel="noreferrer nofollow"
              >
                {data.site.siteMetadata.eventLinkText}
              </a>
            </p>
          )}
          <Sponsor layout="side" className={pageStyles.sponsorWrapper} />
        </div>
        <ul className={pageStyles.storyGrid}>
          {articles.map((storyMeta, index) => {
            const {
              overrides = {},
              imgAlt,
              arcId,
            } = storyMeta;

            const story = find(data.allArcStory.nodes, { storyId: arcId });
            if (!story) {
              if (process.env.NODE_ENV === 'development') {
                console.error('story not found', storyMeta);
              }
              return null;
            }

            const imageUrl = get(overrides, 'image', '').trim()
              || get(story, 'promo_items.basic.url', '').trim()
              || backupImage;

            return (
              <StoryTile
                headline={get(overrides, 'headline', story.headlines.web || story.headlines.basic)}
                href={`/articles/${slugify(story.headlines.basic)}/`}
                imgAlt={imgAlt}
                key={arcId}
                image={imageUrl}
                isDisplayed={shuffledOrder[index] <= displayFrame}
              />
            );
          })}
        </ul>
      </main>
    </Layout>
  );
};

export default IndexPage;
